import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useOrganization } from '../contexts/OrganizationContext';
import { Trash2, Mail, UserPlus, Shield, Eye, Settings, Users, Copy, Check, Building2, Edit2, Save, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const ROLE_DESCRIPTIONS = {
  admin: {
    label: 'Admin',
    description: 'Full access to all features including billing and team management',
    icon: Shield,
    color: 'bg-red-500',
  },
  manager: {
    label: 'Manager',
    description: 'Can manage stores, products, and approve jobs',
    icon: Settings,
    color: 'bg-blue-500',
  },
  operator: {
    label: 'Operator',
    description: 'Can view data and create jobs',
    icon: Users,
    color: 'bg-green-500',
  },
  viewer: {
    label: 'Viewer',
    description: 'Read-only access to view data',
    icon: Eye,
    color: 'bg-gray-500',
  },
};

export const TeamManagement: React.FC = () => {
  const { toast } = useToast();
  const {
    currentOrganization,
    members,
    invitations,
    isLoading,
    inviteMember,
    removeMember,
    updateMemberRole,
    updateOrganization,
    cancelInvitation,
    refreshMembers,
    refreshInvitations,
    isOrgAdmin,
    hasPermission,
  } = useOrganization();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('viewer');
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const [inviteToCancel, setInviteToCancel] = useState<string | null>(null);
  const [invitationUrl, setInvitationUrl] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const [isEditingOrgName, setIsEditingOrgName] = useState(false);
  const [editedOrgName, setEditedOrgName] = useState('');

  const handleInvite = async () => {
    if (!inviteEmail || !inviteRole) {
      toast({
        title: 'Error',
        description: 'Please enter an email and select a role',
        variant: 'destructive',
      });
      return;
    }

    try {
      const url = await inviteMember(inviteEmail, inviteRole);
      if (url) {
        setInvitationUrl(url);
        toast({
          title: 'Invitation Created',
          description: 'Copy the invitation link to share with the user',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send invitation',
        variant: 'destructive',
      });
    }
  };

  const handleCopyInvitationUrl = async () => {
    if (!invitationUrl) return;
    
    try {
      await navigator.clipboard.writeText(invitationUrl);
      setCopiedUrl(true);
      toast({
        title: 'Copied!',
        description: 'Invitation link copied to clipboard',
      });
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedUrl(false);
      }, 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        variant: 'destructive',
      });
    }
  };

  const handleCopyExistingInvitationUrl = async (invitationId: string, url: string) => {
    if (!url) {
      toast({
        title: 'Error',
        description: 'Invitation URL not available',
        variant: 'destructive',
      });
      return;
    }
    
    try {
      await navigator.clipboard.writeText(url);
      setCopiedInviteId(invitationId);
      toast({
        title: 'Copied!',
        description: 'Invitation link copied to clipboard',
      });
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedInviteId(null);
      }, 2000);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy link',
        variant: 'destructive',
      });
    }
  };

  const handleCloseInviteDialog = () => {
    setShowInviteDialog(false);
    setInviteEmail('');
    setInviteRole('viewer');
    setInvitationUrl(null);
    setCopiedUrl(false);
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    try {
      await removeMember(memberToRemove);
      toast({
        title: 'Success',
        description: 'Team member removed',
      });
      setMemberToRemove(null);
      // Explicitly refresh members list
      await refreshMembers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove team member',
        variant: 'destructive',
      });
      // Try to refresh even on error to get latest state
      await refreshMembers();
    }
  };

  const handleCancelInvitation = async () => {
    if (!inviteToCancel) return;

    try {
      await cancelInvitation(inviteToCancel);
      toast({
        title: 'Success',
        description: 'Invitation cancelled',
      });
      setInviteToCancel(null);
      // Explicitly refresh invitations list
      await refreshInvitations();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to cancel invitation',
        variant: 'destructive',
      });
      // Try to refresh even on error to get latest state
      await refreshInvitations();
    }
  };

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      await updateMemberRole(memberId, newRole);
      toast({
        title: 'Success',
        description: 'Role updated successfully',
      });
      // Explicitly refresh members list
      await refreshMembers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update role',
        variant: 'destructive',
      });
      // Try to refresh even on error to get latest state
      await refreshMembers();
    }
  };

  const handleStartEditOrgName = () => {
    if (currentOrganization) {
      setEditedOrgName(currentOrganization.name);
      setIsEditingOrgName(true);
    }
  };

  const handleSaveOrgName = async () => {
    if (!editedOrgName.trim()) {
      toast({
        title: 'Error',
        description: 'Organization name cannot be empty',
        variant: 'destructive',
      });
      return;
    }

    try {
      await updateOrganization(editedOrgName.trim());
      toast({
        title: 'Success',
        description: 'Organization name updated successfully',
      });
      setIsEditingOrgName(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update organization name',
        variant: 'destructive',
      });
    }
  };

  const handleCancelEditOrgName = () => {
    setIsEditingOrgName(false);
    setEditedOrgName('');
  };

  if (!currentOrganization) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">No organization selected</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Team Management</h1>
          <p className="text-muted-foreground mt-2">
            Manage your team members and their permissions
          </p>
        </div>
        {hasPermission('team', 'invite') && (
          <Button onClick={() => setShowInviteDialog(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Invite Member
          </Button>
        )}
      </div>

      <Tabs defaultValue="organization" className="space-y-4">
        <TabsList>
          <TabsTrigger value="organization">Organization Settings</TabsTrigger>
          <TabsTrigger value="members">Team Members ({members.length})</TabsTrigger>
          <TabsTrigger value="invitations">Pending Invitations ({invitations.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="organization" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>Manage your organization's name and settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Organization Name */}
              <div className="space-y-2">
                <Label>Organization Name</Label>
                {isEditingOrgName ? (
                  <div className="flex items-center space-x-2">
                    <Input
                      value={editedOrgName}
                      onChange={(e) => setEditedOrgName(e.target.value)}
                      placeholder="Enter organization name"
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveOrgName();
                        if (e.key === 'Escape') handleCancelEditOrgName();
                      }}
                    />
                    <Button
                      size="sm"
                      onClick={handleSaveOrgName}
                      disabled={isLoading}
                    >
                      <Save className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCancelEditOrgName}
                      disabled={isLoading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-medium">{currentOrganization?.name}</span>
                    {isOrgAdmin() && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleStartEditOrgName}
                        disabled={isLoading}
                      >
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Organization Slug */}
              <div className="space-y-2">
                <Label>Organization Slug</Label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 px-3 py-2 bg-muted rounded-md">
                    <code className="text-sm">{currentOrganization?.slug}</code>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(currentOrganization?.slug || '');
                      toast({
                        title: 'Copied',
                        description: 'Organization slug copied to clipboard',
                      });
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  The slug is automatically generated from your organization name and is used in URLs and API calls.
                </p>
              </div>

              {/* Organization ID */}
              <div className="space-y-2">
                <Label>Organization ID</Label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1 px-3 py-2 bg-muted rounded-md">
                    <code className="text-xs">{currentOrganization?.id}</code>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(currentOrganization?.id || '');
                      toast({
                        title: 'Copied',
                        description: 'Organization ID copied to clipboard',
                      });
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Created Date */}
              <div className="space-y-2">
                <Label>Created</Label>
                <p className="text-sm text-muted-foreground">
                  {currentOrganization?.created_at 
                    ? new Date(currentOrganization.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })
                    : 'Unknown'}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="space-y-4">
          {members.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">
                  No team members yet. Invite someone to get started!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {members.map((member) => {
                const roleInfo = ROLE_DESCRIPTIONS[member.role as keyof typeof ROLE_DESCRIPTIONS];
                const Icon = roleInfo?.icon || Eye;
                
                return (
                  <Card key={member.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`p-2 rounded-lg ${roleInfo?.color || 'bg-gray-500'} bg-opacity-10`}>
                            <Icon className={`h-5 w-5 ${roleInfo?.color?.replace('bg-', 'text-')}`} />
                          </div>
                          <div>
                            <p className="font-medium">{member.email}</p>
                            <p className="text-sm text-muted-foreground">
                              Joined {new Date(member.joined_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          {hasPermission('team', 'edit') && member.user_id !== member.id ? (
                            <Select
                              value={member.role}
                              onValueChange={(value) => handleRoleChange(member.id, value)}
                              disabled={isLoading}
                            >
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(ROLE_DESCRIPTIONS).map(([role, info]) => (
                                  <SelectItem key={role} value={role}>
                                    {info.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge variant="secondary">{roleInfo?.label}</Badge>
                          )}
                          
                          {hasPermission('team', 'remove') && member.user_id !== member.id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => setMemberToRemove(member.id)}
                              disabled={isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="invitations" className="space-y-4">
          {invitations.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">
                  No pending invitations
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {invitations.map((invitation) => {
                const roleInfo = ROLE_DESCRIPTIONS[invitation.role as keyof typeof ROLE_DESCRIPTIONS];
                
                return (
                  <Card key={invitation.id}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <Mail className="h-5 w-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{invitation.email}</p>
                            <p className="text-sm text-muted-foreground">
                              Expires {new Date(invitation.expires_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline">{roleInfo?.label || invitation.role}</Badge>
                          {hasPermission('team', 'invite') && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleCopyExistingInvitationUrl(invitation.id, invitation.invitation_url || '')}
                                disabled={isLoading || !invitation.invitation_url}
                                title="Copy invitation link"
                              >
                                {copiedInviteId === invitation.id ? (
                                  <Check className="h-4 w-4 text-green-500" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setInviteToCancel(invitation.id)}
                                disabled={isLoading}
                                title="Cancel invitation"
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Invite Member Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={handleCloseInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              {invitationUrl 
                ? 'Share this invitation link with the new team member'
                : 'Create an invitation link to share with a new team member'
              }
            </DialogDescription>
          </DialogHeader>
          
          {!invitationUrl ? (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_DESCRIPTIONS).map(([role, info]) => (
                        <SelectItem key={role} value={role}>
                          {info.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {ROLE_DESCRIPTIONS[inviteRole as keyof typeof ROLE_DESCRIPTIONS]?.description}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleCloseInviteDialog}>
                  Cancel
                </Button>
                <Button onClick={handleInvite} disabled={isLoading}>
                  Create Invitation Link
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Invitation Details</Label>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Email: <span className="font-medium text-foreground">{inviteEmail}</span></p>
                    <p>Role: <span className="font-medium text-foreground">{ROLE_DESCRIPTIONS[inviteRole as keyof typeof ROLE_DESCRIPTIONS].label}</span></p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Invitation Link</Label>
                  <div className="flex gap-2">
                    <Input
                      value={invitationUrl}
                      readOnly
                      className="font-mono text-xs"
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={handleCopyInvitationUrl}
                      className="shrink-0"
                    >
                      {copiedUrl ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This link will expire in 7 days
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCloseInviteDialog}>
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this team member? They will lose access to the organization immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember}>
              Remove Member
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Invitation Confirmation */}
      <AlertDialog open={!!inviteToCancel} onOpenChange={() => setInviteToCancel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to cancel this invitation? The invitation link will no longer be valid.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Invitation</AlertDialogCancel>
            <AlertDialogAction onClick={handleCancelInvitation}>
              Cancel Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};